import './../styles.css';
import { lazy, Suspense } from 'react';
import { useEngine } from '../store';
import type { SaveStorage } from '@platform/SaveStorage';
import { useSaveManager } from '../useSaveManager';
import { useRoute } from '../router';
import { Landing } from './Landing';
import { FaqPage, AboutPage, PressPage, UpdatesPage, NotFoundPage } from './Pages';
import { DevlogIndexPage, DevlogPostPage, DEVLOG_ROUTES } from './Devlog';
import { SiteFooter } from './SiteChrome';
import { WIKI_SECTIONS, wikiPath } from '../wikiNav';
import { SITE } from '../siteMeta';

// The Pixi-using routes are lazy-loaded so PixiJS stays OUT of the landing/entry
// bundle (it only downloads when you visit /play, /mechs, /wiki or /riglab). The
// route table imports from ../wikiNav (Pixi-free) for the same reason; importing
// the Wiki component here would drag Pixi back into the entry chunk.
const GameView = lazy(() => import('./GameView').then((m) => ({ default: m.GameView })));
const Wiki = lazy(() => import('./Wiki').then((m) => ({ default: m.Wiki })));
const MechShowcase = lazy(() => import('./MechShowcase').then((m) => ({ default: m.MechShowcase })));
const RigLab = lazy(() => import('./RigLab').then((m) => ({ default: m.RigLab })));

/**
 * Every routable page. The prerender script (scripts/prerender.mjs) reads this
 * off window.__bmeRoutes to emit one static HTML file per route, which is what
 * makes the site indexable on a static host.
 */
export const ROUTES: readonly string[] = [
  '/',
  '/play',
  '/mechs',
  '/faq',
  '/about',
  '/press',
  '/updates',
  ...DEVLOG_ROUTES,
  ...WIKI_SECTIONS.map((s) => wikiPath(s)),
];

declare global {
  interface Window { __bmeRoutes?: readonly string[]; __bmeSiteOrigin?: string }
}
if (typeof window !== 'undefined') {
  window.__bmeRoutes = ROUTES;
  window.__bmeSiteOrigin = SITE.origin;
}

/**
 * Top-level router. The save lifecycle (offline catch-up, autosave) lives here so
 * it runs regardless of which page is shown; the engine ticks globally from the
 * host.
 */
export function Site({ storage, booted }: { storage: SaveStorage; booted: boolean }) {
  const engine = useEngine();
  const save = useSaveManager(engine, storage, booted);
  const route = useRoute();

  // Dev QA workbench: no site chrome, deliberately absent from ROUTES.
  if (route === '/riglab') {
    return (
      <Suspense fallback={null}>
        <RigLab />
      </Suspense>
    );
  }

  // Every real page renders the shared footer once, here, so no page can forget
  // it. The page owns its own header (TopBar on /play, SiteNav elsewhere).
  const page =
    route === '/play' ? <GameView save={save} booted={booted} /> :
    route === '/wiki' ? <Wiki slug="" /> :
    route.startsWith('/wiki/') ? <Wiki slug={route.slice('/wiki/'.length)} /> :
    route === '/mechs' ? <MechShowcase /> :
    route === '/faq' ? <FaqPage /> :
    route === '/about' ? <AboutPage /> :
    route === '/press' ? <PressPage /> :
    route === '/updates' ? <UpdatesPage /> :
    route === '/devlog' ? <DevlogIndexPage /> :
    route.startsWith('/devlog/') ? <DevlogPostPage slug={route.slice('/devlog/'.length)} /> :
    route === '/' ? <Landing /> :
    <NotFoundPage />;

  return (
    <>
      <Suspense fallback={<div style={{ minHeight: '70vh' }} />}>{page}</Suspense>
      <SiteFooter />
    </>
  );
}
